import type React from "react";

import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { useGetPatientCareEventsQuery } from "api/hooks/useGetPatientCareEventsQuery";
import type { CareEvent } from "api/models/CareEvent";
import { useProfileContext } from "contexts/ProfileContext";
import { Notification } from "shared/atoms/Notification";
import { AccordionListSkeleton } from "shared/atoms/SkeletonLayout";
import type { Market, MedicalNoteType } from "types";
import type { DateString } from "types/DateString";
import { useLocalizedDate } from "utils/date";
import { getMedicalNoteConfig } from "utils/medicalNotes";

import { isCareEvent } from "../../utils/typeCheckers";
import { RecordDynamicNote } from "../RecordDynamicNote";
import { RecordNote } from "../RecordNote";

export const RecordNoteList: React.FC = () => {
  const { t } = useTranslation();
  const { parseISO } = useLocalizedDate();
  const { profile } = useProfileContext();
  const patientId = Number(useParams().patientId);

  const { data, isLoading, error } = useGetPatientCareEventsQuery({ patientId, signed: true });

  const compareNotes = (note1: CareEvent, note2: CareEvent) => {
    if (note2.start_time === null) return 1;
    if (note1.start_time === null) return -1;
    const date1 = parseISO(note1.start_time);
    const date2 = parseISO(note2.start_time);
    if (date1 === date2) return 0;
    return date1 < date2 ? 1 : -1;
  };

  return (
    <>
      {!isLoading && !error && data?.length === 0 && (
        <Typography textAlign="center" p={2}>
          {t("patients.medical_records.no_signed_medical_records")}
        </Typography>
      )}
      {!isLoading &&
        !error &&
        data &&
        data.sort(compareNotes).map(record => {
          if (!isCareEvent(record)) {
            return null;
          }

          if (record.medical_record_note === null) {
            return (
              <RecordDynamicNote
                key={record.id}
                record={record}
                config={getMedicalNoteConfig(
                  profile?.market as Market,
                  record.label as MedicalNoteType,
                  record.signed_at as DateString
                )}
              />
            );
          }

          return <RecordNote key={record.id} record={record} />;
        })}
      {isLoading && <AccordionListSkeleton dataTestId="loading" />}
      {error && <Notification variant="danger">{t("errors.generic")}</Notification>}
    </>
  );
};
